import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { LoadingSpinner } from '../generic/Loading';
import UnitCard from './unit-card/UnitCard';
import { possibleUnitMoves } from '../generic/drag-helper/utils';
import { getCourseOutlineChildren } from './data/api';

/**
 * SubsectionChildren
 * - Fetches units (verticals) for a subsection on demand using course_index children endpoint.
 * - Shows "Load units" button per subsection and "Load more units" if page_meta indicates more.
 * - If subsection already has childInfo.children (preloaded), renders them immediately without fetching.
 */
const SubsectionChildren = ({
  courseId,
  section,
  subsection,
  sectionIndex,
  subsectionIndex,
  sections,
  isSelfPaced,
  isCustomRelativeDatesActive,
  savingStatus,
  openPublishModal,
  openConfigureModal,
  openDeleteModal,
  handleEditSubmit,
  handleDuplicateUnitSubmit,
  handleNewUnitSubmit,
  handlePasteClipboardClick,
  updateUnitOrderByIndex,
  discussionsSettings,
  demoUnitKeys: initialDemoUnitKeys,
  // forwarder
  getTitleLink,
}) => {
  const initialUnits = subsection?.childInfo?.children || [];
  const [demoUnitKeys, setDemoUnitKeys] = useState(
    Array.isArray(initialDemoUnitKeys) ? initialDemoUnitKeys : [],
  );
  const normalizeUnits = (unitList, keys) => {
    const keySet = new Set(Array.isArray(keys) ? keys : []);
    return unitList.map((unit) => {
      const resolvedDemoUnitId =
        unit.demoUnitId
        || unit.demoUnit?.id
        || unit.demoUnit?.pk
        || unit.demo_unit_id
        || null;
      return {
        ...unit,
        isDemoUnit: unit.isDemoUnit ?? keySet.has(unit.id),
        demoUnitId: resolvedDemoUnitId,
      };
    });
  };
  const [loaded, setLoaded] = useState(Boolean(initialUnits && initialUnits.length));
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [units, setUnits] = useState(() => normalizeUnits(initialUnits, demoUnitKeys));
  const [pageMeta, setPageMeta] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (Array.isArray(initialDemoUnitKeys)) {
      setDemoUnitKeys(initialDemoUnitKeys);
      setUnits((prev) => normalizeUnits(prev, initialDemoUnitKeys));
    }
  }, [initialDemoUnitKeys]);

  const computePossibleUnitMoves = useMemo(() => (
    (index, step) => possibleUnitMoves([...sections], sectionIndex, subsectionIndex, section, subsection, units, index, step)
  ), [sections, sectionIndex, subsectionIndex, section, subsection, units]);

  const loadUnits = async () => {
    if (isLoading || !courseId || !subsection?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const resp = await getCourseOutlineChildren(courseId, {
        parent: subsection.id,
        limit: 20,
        offset: 0,
        format: 'full',
        prefetchDepth: 0,
      });

      const fetched = resp.sections || resp.course_structure?.child_info?.children || resp.children || [];
      const respPageMeta = resp.pageMeta || resp.page_meta || null;
      const respDemoUnitKeys = resp.demoUnitKeys || resp.demo_unit_keys || null;
      const keysToUse = Array.isArray(respDemoUnitKeys) ? respDemoUnitKeys : demoUnitKeys;

      if (Array.isArray(respDemoUnitKeys)) {
        setDemoUnitKeys(respDemoUnitKeys);
      }
      setUnits(normalizeUnits(fetched, keysToUse));
      setPageMeta(respPageMeta);
      setLoaded(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load units for subsection', subsection.id, err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreUnits = async () => {
    if (isLoadingMore || !pageMeta) return;
    setIsLoadingMore(true);
    setError(null);
    try {
      const limit = pageMeta.limit ?? pageMeta.page_size ?? 20;
      const offset = pageMeta.offset ?? (units.length || 0);
      const nextOffset = offset + limit;

      const resp = await getCourseOutlineChildren(courseId, {
        parent: subsection.id,
        limit,
        offset: nextOffset,
        format: 'full',
        prefetchDepth: 0,
      });

      const more = resp.sections || resp.course_structure?.child_info?.children || resp.children || [];
      const respPageMeta = resp.pageMeta || resp.page_meta || null;
      const respDemoUnitKeys = resp.demoUnitKeys || resp.demo_unit_keys || null;
      const keysToUse = Array.isArray(respDemoUnitKeys) ? respDemoUnitKeys : demoUnitKeys;

      setUnits((prev) => {
        const existing = new Set(prev.map((u) => u.id));
        const toAppend = more.filter((m) => !existing.has(m.id));
        return prev.concat(normalizeUnits(toAppend, keysToUse));
      });

      if (Array.isArray(respDemoUnitKeys)) {
        setDemoUnitKeys(respDemoUnitKeys);
      }

      if (respPageMeta) {
        setPageMeta(respPageMeta);
      } else {
        setPageMeta((prev) => ({ ...(prev || {}), offset: (prev?.offset ?? 0) + limit }));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load more units for subsection', subsection.id, err);
      setError(err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleDemoUnitChange = (unitId, isDemoUnit, demoUnitId) => {
    setUnits((prev) => prev.map((unit) => (
      unit.id === unitId
        ? { ...unit, isDemoUnit, demoUnitId: demoUnitId ?? unit.demoUnitId }
        : unit
    )));

    setDemoUnitKeys((prev) => {
      const set = new Set(prev);
      if (isDemoUnit) {
        set.add(unitId);
      } else {
        set.delete(unitId);
      }
      return Array.from(set);
    });
  };

  if (!loaded) {
    return (
      <div className="py-2">
        <button
          type="button"
          className="btn btn-link p-0"
          onClick={loadUnits}
          disabled={isLoading}
        >
          {isLoading ? <LoadingSpinner /> : 'Load units'}
        </button>
        {error && <div className="text-danger small">Failed to load units</div>}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="py-3">
        <LoadingSpinner />
      </div>
    );
  }

  if (!units || units.length === 0) {
    return (
      <div className="py-2 text-muted small">
        No units
      </div>
    );
  }

  const hasMore = (() => {
    if (!pageMeta) return false;
    const total = pageMeta.total_count ?? pageMeta.totalCount ?? pageMeta.count ?? null;
    const limit = pageMeta.limit ?? pageMeta.page_size ?? null;
    if (total === null) return units.length === (limit || 0);
    return units.length < total;
  })();

  return (
    <>
      <SortableContext
        id={subsection.id}
        items={units}
        strategy={verticalListSortingStrategy}
      >
        {units.map((unit, unitIndex) => (
          <UnitCard
            key={unit.id}
            unit={unit}
            subsection={subsection}
            section={section}
            courseId={courseId}
            isSelfPaced={isSelfPaced}
            isCustomRelativeDatesActive={isCustomRelativeDatesActive}
            index={unitIndex}
            getPossibleMoves={computePossibleUnitMoves}
            savingStatus={savingStatus}
            onOpenPublishModal={openPublishModal}
            onOpenConfigureModal={openConfigureModal}
            onOpenDeleteModal={openDeleteModal}
            onEditSubmit={handleEditSubmit}
            onDuplicateSubmit={handleDuplicateUnitSubmit}
            onOrderChange={updateUnitOrderByIndex}
            onCopyToClipboardClick={handlePasteClipboardClick}
            discussionsSettings={discussionsSettings}
            onDemoUnitChange={handleDemoUnitChange}
            // forward it to UnitCard
            getTitleLink={getTitleLink}
          />
        ))}
      </SortableContext>

      {hasMore && (
        <div className="py-2">
          <button
            type="button"
            className="btn btn-link p-0"
            onClick={loadMoreUnits}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? <LoadingSpinner /> : 'Load more units'}
          </button>
          {error && <div className="text-danger small">Failed to load more units</div>}
        </div>
      )}
    </>
  );
};

SubsectionChildren.propTypes = {
  courseId: PropTypes.string.isRequired,
  section: PropTypes.object.isRequired,
  subsection: PropTypes.object.isRequired,
  sectionIndex: PropTypes.number.isRequired,
  subsectionIndex: PropTypes.number.isRequired,
  sections: PropTypes.array.isRequired,
  isSelfPaced: PropTypes.bool,
  isCustomRelativeDatesActive: PropTypes.bool,
  savingStatus: PropTypes.string,
  openPublishModal: PropTypes.func,
  openConfigureModal: PropTypes.func,
  openDeleteModal: PropTypes.func,
  handleEditSubmit: PropTypes.func,
  handleDuplicateUnitSubmit: PropTypes.func,
  handleNewUnitSubmit: PropTypes.func,
  handlePasteClipboardClick: PropTypes.func,
  updateUnitOrderByIndex: PropTypes.func,
  discussionsSettings: PropTypes.object,
  demoUnitKeys: PropTypes.array,
  getTitleLink: PropTypes.func,
};

SubsectionChildren.defaultProps = {
  isSelfPaced: false,
  isCustomRelativeDatesActive: false,
  savingStatus: '',
  openPublishModal: () => {},
  openConfigureModal: () => {},
  openDeleteModal: () => {},
  handleEditSubmit: () => {},
  handleDuplicateUnitSubmit: () => {},
  handleNewUnitSubmit: () => {},
  handlePasteClipboardClick: () => {},
  updateUnitOrderByIndex: () => {},
  discussionsSettings: {},
  demoUnitKeys: [],
  getTitleLink: () => {},
};

export default SubsectionChildren;